import { Button } from '@/components/ui/button'
import { Cookie, ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

const COOKIE_CONSENT_KEY = 'ghostflow-cookie-consent'

type ConsentStatus = 'accepted' | 'rejected' | null

function getConsentStatus(): ConsentStatus {
  try {
    const value = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (value === 'accepted' || value === 'rejected') return value
    return null
  } catch {
    return null
  }
}

function setConsentStatus(status: 'accepted' | 'rejected') {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, status)
  } catch {
    // localStorage may be unavailable
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Show banner only if user hasn't made a choice yet
    const status = getConsentStatus()
    if (status === null) {
      // Small delay so it doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 500)
      return () => clearTimeout(timer)
    }
  }, [])

  if (!visible) return null

  const handleAccept = () => {
    setConsentStatus('accepted')
    setVisible(false)
  }

  const handleReject = () => {
    setConsentStatus('rejected')
    setVisible(false)
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-100 p-4 animate-in slide-in-from-bottom-4 duration-300">
      <div className="mx-auto max-w-4xl rounded-lg border bg-background/95 backdrop-blur-sm shadow-lg p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Cookie Notice</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                We use essential cookies to keep you signed in and ensure the platform works correctly.
                We also use analytics cookies to improve our service.
                Read our{' '}
                <Link
                  to="/privacy"
                  className="underline underline-offset-2 hover:text-foreground inline-flex items-center gap-0.5"
                >
                  Privacy Policy
                  <ExternalLink className="h-3 w-3" />
                </Link>{' '}
                for more details.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReject}
              className="text-xs"
            >
              Reject Non-Essential
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              className="text-xs"
            >
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
